/**
 * Restringir acceso a una carpeta (spec 2026-10-07 §3).
 *
 * Un doc por carpeta restringida: folders/{cid}/restricciones/{docId}, con
 * docId = ruta de la colección de la carpeta con '/' → '~'
 * (lib/folder_restrictions.docIdForPath). `uids` es la lista FINAL de
 * restringidos de esa carpeta (o se calcula aplicando addedUids/removedUids
 * sobre la vigente); si queda vacía se borra el doc. El doc y la
 * auditoría (FOLDERS.RESTRICT) van en UN batch. Solo rango ≥ 60 (lib/rank);
 * las reglas lo exigen también.
 */
import { FOLDERS } from '../../../application/types';
import { RESTRICCIONES, docIdForPath } from '../../lib/folder_restrictions';
import { canRestrict } from '../../lib/rank';

export const restrictions = {

  // `addedUids`/`removedUids` (lo que el editor cambió) se aplican sobre la lista
  // VIGENTE leída al guardar, así no se pierden los cambios que otro editor hizo
  // mientras el panel estaba abierto. Sin ellos, `uids` es la lista final.
  set: ({ editorRole, cid, collectionPath, uids, addedUids, removedUids }) => ( dispatch, state, firebase ) => {

    dispatch({ type: FOLDERS.RESTRICT.RESTRICTING_FOLDER });

    return new Promise( async ( resolve, reject ) => {
      const fail = ( code, message ) => reject( dispatch({ type: FOLDERS.RESTRICT.ERROR, code, message }) );
      if ( !canRestrict( editorRole ) ) return fail( 0, 'Solo contador, supervisora o root pueden restringir carpetas.' );
      const docId = docIdForPath( collectionPath );
      if ( !cid || !docId || collectionPath.split('/')[1] !== cid ) return fail( 0, 'La carpeta no tiene una ruta válida.' );
      try {
        const db = firebase().firestore();
        const actor = firebase().auth().currentUser.uid;
        const ts = firebase().firestore.FieldValue.serverTimestamp();
        const ref = db.collection('folders').doc( cid ).collection( RESTRICCIONES ).doc( docId );
        const snap = await ref.get();
        const before = snap.exists ? snap.data() : null;
        const merged = ( addedUids || removedUids ) ?
          [ ...( ( before && before.uids ) || [] ).filter( u => !( removedUids || [] ).includes( u ) ), ...( addedUids || [] ) ] :
          ( uids || [] );
        const clean = [ ...new Set( merged.filter( u => typeof u === 'string' && u ) ) ].sort();
        if ( !before && !clean.length ) {
          return resolve( dispatch({ type: FOLDERS.RESTRICT.RESTRICTED_FOLDER, data: null }) );
        }
        const batch = db.batch();
        const log = db.collection('audit').doc();
        batch.set( log, {
          uid: actor,
          eventType: FOLDERS.RESTRICT.RESTRICT_FOLDER,
          eventDate: ts,
          resourcePath: ref.path,
          folderPath: collectionPath,
          action: clean.length ? ( before ? 'update' : 'create' ) : 'delete',
        });
        if ( before ) batch.set( log.collection('before').doc(), before );
        if ( clean.length ) {
          batch.set( log.collection('after').doc(), { path: collectionPath, uids: clean } );
          batch.set( ref, { path: collectionPath, uids: clean, updatedBy: db.collection('users').doc( actor ), updatedAt: ts } );
        } else {
          batch.delete( ref );
        }
        await batch.commit();
        return resolve( dispatch({
          type: FOLDERS.RESTRICT.RESTRICTED_FOLDER,
          data: clean.length ? { path: collectionPath, uids: clean } : null,
        }) );
      } catch ( error ) {
        return fail( error.code, error.message );
      }
    });
  },

};

export default restrictions;
